import { CommonModule, Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { userRoles } from '../../../environments/environment';
import { TableComponent } from '../../shared/components/custom-table/table.component';
import { AddStudentComponent } from '../../shared/components/dialog/add-student/add-student.component';
import { AddTeacherComponent } from '../../shared/components/dialog/add-teacher/add-teacher.component';
import {
  AlertDialogComponent,
  AlertDialogData,
} from '../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { CommonDialogComponent } from '../../shared/components/dialog/commom/commom-dialog.component';
import { UserViewDialogComponent } from '../../shared/components/dialog/view-user-details/view-dialog.component';
import { ExportDropdownComponent } from '../../shared/components/export-dropdown/export-dropdown';
import { InputComponent } from '../../shared/components/from-input/input.component';
import { CustomToastComponent } from '../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../shared/services/http.service';

interface TeacherDialogData {
  initialData?: User;
  EditMode: boolean;
}
function isPagedResponse(
  data: UsersPagedResponse | User[],
): data is UsersPagedResponse {
  return !!(data as UsersPagedResponse).items;
}

interface APIResponse {
  items: any;
  totalRecords?: any;
  totalRecord?: any;
  userId: string;
  fullName: string;
}

interface User {
  userId: string;
  teacherId?: string;
  studentId?: string;
  memberId?: string;
  fullName: string;
  roleName: string;
  schoolName: string;
  status: string;
  email: string;
  yearLevel?: number;
  dateOfBirth?: string;
  firstName?: string;
  lastName?: string;
  mobileNumber?: string;
  parentGuardianEmail?: string;
  parentGuardianFullName?: string;
  personalEmail?: string;
  ethnicity?: string;
  gender?: string;
  schoolId?: string;
}
interface UsersPagedResponse {
  items: User[];
  totalRecords?: number;
  totalRecord?: number;
}
interface ApiDataResponse {
  status: boolean;
  message: string;
  data: APIResponse;
}

interface CSQTeamMember {
  id: string;
  memberId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  role: string;
}

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    TableComponent,
    MatSnackBarModule,
    MatIconModule,
    MatMenuModule,
    MatDialogModule,
    FormsModule,
    ExportDropdownComponent,
  ],
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('actionButtons', { static: true })
  actionButtonsTemplate!: TemplateRef<any>;
  @ViewChild('addCSQContent') addCSQContent!: TemplateRef<any>;
  @ViewChild(TableComponent) tableComponent!: TableComponent;

  userForm!: FormGroup;
  csqForm!: FormGroup;
  searchControl = new FormControl('');
  columns: string[] = ['name', 'role', 'email', 'school', 'status'];
  exportColumns: string[] = ['Name', 'Role', 'Email', 'School', 'Status'];
  originalData: any[] = [];
  data: any[] = [];
  fullData: any[] = [];
  loading = false;
  dataLoading = false;
  csqLoading = false;
  isSearchActive = false;
  isProcessing = false;
  search: string = '';
  searchId: string = '';
  Role = localStorage.getItem('userRole') || '';

  currentPage: number = 1;
  pageSize: number = 10;
  totalRecords: number = 0;
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | '' = '';
  sortableColumns: string[] = ['name', 'role', 'email', 'school', 'status'];
  private isPageChangeLoading = false;
  private currentRequestId = 0;

  showStatusDropdown = false;
  userRoles = {
    add: {
      addCSQUser: false,
      addTeacher: false,
      addStudent: false,
      addAdmin: false,
      addSuperAdmin: false,
    },
    export: false,
    listItem: {
      view: false,
      edit: false,
      delete: false,
      statusChange: false,
    },
  };

  role: any[] = [
    { label: 'CSQ User', value: 'CSQ User' },
    { label: 'Event Manager', value: 'Event Manager' },
    { label: 'Teacher', value: 'Teacher' },
    { label: 'Student', value: 'Student' },
  ];
  status: any[] = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' },
    { label: 'Pending', value: 'Pending' },
  ];

  customErrorMessages = {
    firstName: {
      required: 'Please enter a first name.',
      whitespace: 'Please enter a first name',
    },

    lastName: {
      required: 'Please enter a last name.',
      whitespace: 'Please enter a last name',
    },
    email: {
      required: 'Please enter a email address.',
      email: 'Please enter a valid email address.',
    },
    mobileNumber: {
      required: 'Please enter a phone number.',
      pattern: 'Please enter a valid phone number.',
    },
  };

  private statusDropdownClickListener: (() => void) | null = null;
  private searchSubscription: any;

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.initForms();

    this.searchSubscription = this.searchControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchValue) => {
        this.search = searchValue || '';
        this.currentPage = 1;
        this.fetchUsers(1);
      });

    this.userForm.valueChanges.subscribe(() => {
      if (this.search.trim().length === 0) {
        this.applyFilter();
      }
    });

    this.route.queryParams.subscribe((params) => {
      this.searchId = params['search'] ?? null;
      this.tryOpenUserModal();
    });

    this.fetchUsers();

    const user = localStorage.getItem('userRole');
    if (user && user in userRoles) {
      this.userRoles =
        userRoles[user as keyof typeof userRoles]?.userManagement;
    }
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  private noEmptyOrWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        control.value === null ||
        control.value === undefined ||
        control.value === ''
      ) {
        return { required: true };
      }
      if (
        typeof control.value === 'string' &&
        control.value.trim().length === 0
      ) {
        return { whitespace: true };
      }

      return null;
    };
  }

  private initForms(): void {
    this.userForm = this.fb.group({
      name: [''],
      role: [''],
      school: [''],
      status: [''],
    });

    this.csqForm = this.fb.group({
      firstName: [
        '',
        [Validators.required, this.noEmptyOrWhitespaceValidator()],
      ],
      lastName: [
        '',
        [Validators.required, this.noEmptyOrWhitespaceValidator()],
      ],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ],
      ],
      mobileNumber: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/),
        ],
      ],
    });
  }

  tryOpenUserModal(): void {
    if (!this.searchId) return;

    const searchedItem = this.fullData.find((item) => item.id == this.searchId);

    if (searchedItem) {
      this.openUserDetailModal(searchedItem);
      return;
    }

    // User not on current page — fetch directly by ID
    this.httpService.get<any>(`users/${this.searchId}`).subscribe({
      next: (response) => {
        if (response?.data) {
          const user = response.data;
          this.openUserDetailModal({
            id: user.userId ?? '-',
            studentId: user.studentId ?? '',
            teacherId: user.teacherId ?? '',
            memberId: user.memberId ?? '',
            name: user.fullName ?? '',
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            mobileNumber: user.mobileNumber ?? '',
            role: user.roleName ?? '',
            school: user.schoolName ?? '',
            email: user.email ?? '',
            yearLevel: user.yearLevel ?? null,
            dateOfBirth: user.dateOfBirth ?? '',
            status: user.status ?? 'Active',
            parentGuardianFullName: user.parentGuardianFullName ?? '',
            parentGuardianEmail: user.parentGuardianEmail ?? '',
            gender: user.gender,
            ethnicity: user.ethnicity,
            personalEmail: user.personalEmail,
            schoolId: user.schoolId,
            isStatusClickable: true,
            ethnicityId: user?.ethnicityId,
            yearLevelId: user?.yearLevelId,
            past: user?.past,
            comp: user?.comp,
          });
        }
      },
      error: () => {},
    });
  }

  private openUserDetailModal(data: any): void {
    this.dialog.open(UserViewDialogComponent, {
      minWidth: '50rem',
      minHeight: '30rem',
      data,
    });
  }

  updateSearch(searchValue: string): void {
    this.searchControl.setValue(searchValue);
  }

  onSort(event: { direction: 'asc' | 'desc' | ''; column: string | null }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    this.currentPage = 1;
    this.fetchUsers(1);
  }

  private getSortColumnName(column: string): string {
    const columnMapping: { [key: string]: string } = {
      name: 'FullName',
      role: 'RoleName',
      email: 'Email',
      school: 'SchoolName',
      status: 'Status',
    };
    return columnMapping[column.toLowerCase()] || column;
  }

  getExportApiUrl(): string {
    if (this.search && this.search.trim() !== '') {
      return 'users/get/search/exports';
    }
    return 'users/exports';
  }

  getCurrentExportParams(): any {
    const params: any = {
      orderby: this.sortDirection || 'asc',
      orderbyname: this.sortColumn
        ? this.getSortColumnName(this.sortColumn)
        : 'FullName',
    };
    if (this.search && this.search.trim() !== '') {
      params.search = this.search;
    }
    return params;
  }

  onExportComplete(type: 'csv' | 'pdf' | 'excel'): void {
    this.showSuccessToast(`Export completed successfully as ${type.toUpperCase()}`);
  }

  fetchUsers(page: number = 1): void {
    this.dataLoading = true;
    const requestId = ++this.currentRequestId;

    let url: string;
    if (this.search && this.search.trim() !== '') {
      url = `users/get/search?search=${encodeURIComponent(this.search)}&page=${page}`;
    } else {
      url = `users?page=${page}`;
    }

    if (this.sortColumn && this.sortDirection) {
      const sortColumnName = this.getSortColumnName(this.sortColumn);
      url += `&orderby=${this.sortDirection}&orderbyname=${sortColumnName}`;
    }

    this.httpService.get<ApiDataResponse>(url).subscribe({
      next: (response) => {
        if (requestId !== this.currentRequestId) return;

        let data = response?.data?.items;
        this.totalRecords = response?.data?.totalRecord || response?.data?.totalRecords || 0;

        this.fullData = data.map((user: any) => ({
          id: user.userId ?? '-',
          studentId: user.studentId ?? '',
          teacherId: user.teacherId ?? '',
          memberId: user.memberId ?? '',
          name: user.fullName ?? '',
          firstName: user.firstName ?? '',
          lastName: user.lastName ?? '',
          mobileNumber: user.mobileNumber ?? '',
          role: user.roleName ?? '',
          school: user.schoolName ?? '',
          _schoolsList: user.schoolName ? [user.schoolName] : [],
          email: user.email ?? '',
          yearLevel: user.yearLevel ?? null,
          dateOfBirth: user.dateOfBirth ?? '',
          status: user.status ?? 'Active',
          parentGuardianFullName: user.parentGuardianFullName ?? '',
          parentGuardianEmail: user.parentGuardianEmail ?? '',
          gender: user.gender,
          ethnicity: user.ethnicity,
          personalEmail: user.personalEmail,
          schoolId: user.schoolId,
          isStatusClickable: true,
          ethnicityId: user?.ethnicityId,
          yearLevelId: user?.yearLevelId,
          past: user?.past,
          comp: user?.comp,
        }));

        this.data = [...this.fullData];
        this.currentPage = page;
        this.isPageChangeLoading = false;
        this.tryOpenUserModal();
      },
      error: (err) => {
        if (requestId !== this.currentRequestId) return;

        this.fullData = [];
        this.data = [];
        this.isPageChangeLoading = false;
        this.showErrorToast(err?.message);
      },
      complete: () => {
        if (requestId === this.currentRequestId) {
          this.dataLoading = false;
        }
      },
    });
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || this.isPageChangeLoading) return;

    this.isPageChangeLoading = true;
    this.currentPage = page;
    this.fetchUsers(page);
  }

  applyFilter(): void {
    this.currentPage = 1;
    this.fetchUsers(1);
  }
  hasFilterInput(): boolean {
    const { name, role, school, status } = this.userForm.value;
    return !!(name?.trim() || role || school?.trim() || status);
  }

  openAddStudentDialog(
    studentData: User | undefined = undefined,
    isEdit: boolean = false,
  ) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    const dialogConfig: MatDialogConfig<TeacherDialogData> = {
      width: '50.5rem',
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: studentData, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    };

    const dialogRef = this.dialog.open(AddStudentComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.fetchUsers();
      this.isProcessing = false;
    });
  }

  openAddTeacherDialog(
    teacherData: User | undefined = undefined,
    isEdit: boolean = false,
  ) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    const dialogConfig: MatDialogConfig<TeacherDialogData> = {
      width: '50.5rem',
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: teacherData, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    };

    const dialogRef = this.dialog.open(AddTeacherComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.fetchUsers();
      this.isProcessing = false;
    });
  }

  openAddCSQUserDialog(
    type: string,
    csqMember: CSQTeamMember | null = null,
  ): void {
    if (this.isProcessing) return;
    this.isProcessing = true;

    if (csqMember) {
      this.csqForm.patchValue({
        firstName: csqMember.firstName || '',
        lastName: csqMember.lastName || '',
        email: csqMember.email || '',
        mobileNumber: csqMember.mobileNumber || '',
      });
    } else {
      this.csqForm.reset();
    }

    const dialogRef = this.dialog.open(CommonDialogComponent, {
      data: {
        title: csqMember ? `Edit ${type}` : `Add new ${type}`,
        confirmText: csqMember ? 'Update' : 'Submit',
        cancelText: 'Cancel',
        template: this.addCSQContent,
        loading: false,
      },
      width: '50.5rem',
      maxWidth: '100vw',
      disableClose: true,
      autoFocus: false,
    });

    dialogRef.componentInstance.confirmed.subscribe((confirmed: boolean) => {
      if (confirmed && this.csqForm.valid) {
        // if (csqMember?.role === 'Super Admin') {
        //     this.saveCSQMember(type, csqMember?.id, dialogRef);
        // } else {
        this.saveCSQMember(type, csqMember, dialogRef);
        // }
      } else if (confirmed) {
        this.markFormGroupTouched(this.csqForm);
        this.scrollToFirstInvalidControl();
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.isProcessing = false;
    });
  }

  private saveCSQMember(
    type: string,
    csqMember?: any,
    dialogRef?: MatDialogRef<any>,
  ): void {
    if (this.csqLoading) return;
    this.csqLoading = true;
    const dialogRefs = this.dialog.openDialogs;

    const currentDialogRef = dialogRefs[dialogRefs.length - 1];

    if (
      currentDialogRef &&
      currentDialogRef.componentInstance instanceof CommonDialogComponent
    ) {
      if (csqMember) {
        currentDialogRef.componentInstance.data.confirmText = 'Updating...';
      } else {
        currentDialogRef.componentInstance.data.confirmText = 'Submitting...';
      }
      currentDialogRef.componentInstance.data.loading = true;
      currentDialogRef.componentInstance.cdr.detectChanges();
    }

    const payload = {
      ...this.csqForm.value,
      ...(type === 'CSQ Admin' && { isCSQAdmin: true }),
      ...(type === 'Super Admin' && { isSuperAdmin: true }),
    };

    const apiCall = csqMember
      ? this.httpService.put(
          `csqusers/${csqMember?.memberId ? csqMember?.memberId : csqMember?.id}`,
          payload,
        )
      : this.httpService.post('csqusers', payload);

    apiCall.subscribe({
      next: (response: any) => {
        if (response.status) {
          this.showSuccessToast(response.message);
          this.fetchUsers();
          dialogRef?.close();
        } else {
          this.showErrorToast(
            response.message || 'Something went wrong. Please try again later.',
          );
          if (
            currentDialogRef &&
            currentDialogRef.componentInstance instanceof CommonDialogComponent
          ) {
            currentDialogRef.componentInstance.data.confirmText = csqMember
              ? 'Update'
              : 'Submit';
            currentDialogRef.componentInstance.data.loading = false;
            currentDialogRef.componentInstance.cdr.detectChanges();
          }
        }
      },
      error: (err) => {
        this.showErrorToast(err?.message);
        this.csqLoading = false;
        if (
          currentDialogRef &&
          currentDialogRef.componentInstance instanceof CommonDialogComponent
        ) {
          currentDialogRef.componentInstance.data.confirmText = csqMember
            ? 'Update'
            : 'Submit';
          currentDialogRef.componentInstance.data.loading = false;
          currentDialogRef.componentInstance.cdr.detectChanges();
        }
      },
      complete: () => {
        this.csqLoading = false;
      },
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      control.updateValueAndValidity();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private scrollToFirstInvalidControl(): void {
    setTimeout(() => {
      const firstInvalidControl = document.querySelector(
        '.ng-invalid:not(form)',
      );
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        (firstInvalidControl as HTMLElement).focus();
      }
    }, 100);
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }

  get statusControl(): FormControl {
    return this.userForm.get('status') as FormControl;
  }

  get userTypeControl(): FormControl {
    return this.userForm.get('role') as FormControl;
  }

  get schoolControl(): FormControl {
    return this.userForm.get('school') as FormControl;
  }

  onView(user: any): void {
    this.dialog.open(UserViewDialogComponent, {
      minWidth: '50rem',
      minHeight: '30rem',
      data: user,
    });
  }

  onEdit(user: any): void {
    if (user.role === 'Teacher') {
      this.openAddTeacherDialog(user, true);
    } else if (user.role === 'Student') {
      this.openAddStudentDialog(user, true);
    } else {
      const csqMember: CSQTeamMember = {
        id: user.id,
        memberId: user.memberId,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        mobileNumber: user.mobileNumber,
        role: user.role,
      };
      const type = user.role;
      this.openAddCSQUserDialog(type, csqMember);
    }
  }

  onDelete(user: any): void {
    const dialogData: AlertDialogData = {
      title: 'Confirm delete',
      message: `Are you sure you want to delete ${user.name}?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.httpService.delete<ApiDataResponse>(`Users/${user.id}`).subscribe({
          next: (response) => {
            if (response?.status) {
              this.updateUserInArrays(user.id, { status: 'Deleted' }, true);
              this.showSuccessToast(response.message);
              this.fetchUsers();
            } else {
              this.showErrorToast(response.message);
            }
          },
          error: (err) => {
            this.showErrorToast(err?.message);
          },
        });
      }
    });
  }

  onStatusClick(event: { row: any; id: any }): void {
    if (!this.userRoles?.listItem?.statusChange) return;
    const { row, id } = event;
    const newStatus =
      row.status === 'Inactive' ? true : row.status === 'Active' ? false : true;
    const newStatusText = newStatus ? 'Active' : 'Inactive';

    const dialogData: AlertDialogData = {
      title: 'Status update',
      message: `Are you sure you want to ${newStatus ? 'Activate' : 'Deactivate'} ${row.name}?`,
      okText: 'Update',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.httpService
          .put<ApiDataResponse>(`users/${id}/toggle-status`, {
            status: newStatus,
          })
          .subscribe({
            next: (response) => {
              if (response?.status) {
                this.fetchUsers();
                this.showSuccessToast(response.message);
              } else {
                this.showErrorToast(response.message);
              }
            },
            error: () => {
              this.showErrorToast(
                'An error occurred while updating the user status.',
              );
            },
          });
      }
    });
  }

  private updateUserInArrays(
    userId: string,
    updates: any,
    remove: boolean = false,
  ): void {
    const originalIndex = this.fullData.findIndex((user) => user.id === userId);
    if (originalIndex !== -1) {
      if (remove) {
        this.fullData.splice(originalIndex, 1);
      } else {
        this.fullData[originalIndex] = {
          ...this.fullData[originalIndex],
          ...updates,
        };
      }
    }

    const filteredIndex = this.data.findIndex((user) => user.id === userId);
    if (filteredIndex !== -1) {
      if (remove) {
        this.data.splice(filteredIndex, 1);
      } else {
        this.data[filteredIndex] = { ...this.data[filteredIndex], ...updates };
      }
    }

    this.data = [...this.data];
  }

  goBack(): void {
    this.location.back();
  }

  toggleStatusDropdown(event: Event): void {
    event.stopPropagation();
    this.showStatusDropdown = !this.showStatusDropdown;

    if (this.showStatusDropdown) {
      this.addStatusDropdownClickListener();
    } else {
      this.removeStatusDropdownClickListener();
    }
    this.cdr.detectChanges();
  }

  closeStatusDropdown(): void {
    this.showStatusDropdown = false;
    this.removeStatusDropdownClickListener();
    this.cdr.detectChanges();
  }

  private addStatusDropdownClickListener(): void {
    this.removeStatusDropdownClickListener();
    this.statusDropdownClickListener = () => {
      this.closeStatusDropdown();
    };
    setTimeout(() => {
      document.addEventListener('click', this.statusDropdownClickListener!);
    }, 0);
  }

  private removeStatusDropdownClickListener(): void {
    if (this.statusDropdownClickListener) {
      document.removeEventListener('click', this.statusDropdownClickListener);
      this.statusDropdownClickListener = null;
    }
  }
}
